/**
 * @fileoverview Change-password mutation hook.
 * @description Wraps POST /identity/password/change in a TanStack mutation. A successful change
 * revokes every session, this one included (the response's loginRequired/sessionsRevoked), so on
 * success this signs the user out locally — clearing the stored token and every cached query — and
 * sends them to /login with a notice that their password changed and they must sign in again. The
 * notice travels as a one-shot sessionStorage flag (password-changed-notice.utils), not router
 * state: signing out makes the current page's route guard redirect to /login too, and that
 * redirect could replace this one and drop its state.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import {
  changePassword,
  type ChangePasswordRequest,
  type ChangePasswordResponse,
} from '@/features/auth/services/change-password.service'
import { markPasswordChangedNotice } from '@/features/auth/utils/password-changed-notice.utils'
import { ROUTES } from '@/shared/constants/routes.constants'
import { useAuth } from '@/shared/lib/auth/useAuth'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Changes the signed-in user's own password, then signs them out.
 * @returns {ReturnType<typeof useMutation<ChangePasswordResponse, ApiError, ChangePasswordRequest>>}
 * The mutation object (mutate, isPending, error, ...).
 */
export function useChangePassword() {
  const { logout } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation<ChangePasswordResponse, ApiError, ChangePasswordRequest>({
    mutationFn: changePassword,
    onSuccess: () => {
      markPasswordChangedNotice()
      logout()
      queryClient.clear()
      void navigate(ROUTES.LOGIN, { replace: true })
    },
  })
}
